import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { getUser, updatePassword, updateUser } from '../users/services/usersService';
import Dialog from '../../components/Dialog';
import { Stack, CircularProgress, TextField, InputAdornment, IconButton, Accordion, AccordionSummary, Typography, AccordionDetails, Grid } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LoadingButton } from '@mui/lab';
import { useIntl, FormattedMessage } from 'react-intl';
import { useAuth } from '../../contexts/authContext';
import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
import Iconify from '../../components/Iconify';
import UserForm from '../users/components/UserForm';

const PasswordSchema = Yup.object().shape({
  password: Yup.string().required('common.form.password.required').min(5, 'common.form.password.requirements').max(50, 'common.form.password.requirements'),
  confirmPassword: Yup.string().required('common.form.password.required').min(5, 'common.form.password.requirements').max(50, 'common.form.password.requirements').oneOf([Yup.ref('password'), null], 'common.form.confirmPassword.requirements')
});

export default function UserProfileDialog({ onClose }) {
  const auth = useAuth();
  const [data, setData] = useState({ isFetching: true });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [expanded, setExpanded] = useState('panel2');

  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handlePasswordUpdate = async (values, actions) => {
    try {
      await updatePassword(values.password);
      enqueueSnackbar(<FormattedMessage id='user.update.msg.success' defaultMessage='User successfully updated' />, {
        variant: 'success',
      });
      actions.resetForm();
    } catch (error) {
      enqueueSnackbar(<FormattedMessage id='user.update.msg.error' defaultMessage='User update failed' />, {
        variant: 'error',
      });
      actions.setSubmitting(false);
    }
  };

  const handleUserUpdate = async (values, actions) => {
    try {
      await updateUser(auth.user.id, values);
      enqueueSnackbar(<FormattedMessage id='user.update.msg.success' defaultMessage='User successfully updated' />, {
        variant: 'success',
      });
      actions.resetForm({ values });
    } catch (error) {
      enqueueSnackbar(<FormattedMessage id='user.update.msg.error' defaultMessage='User update failed' />, {
        variant: 'error',
      });
      actions.setSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = await getUser(auth.user.id);
        enqueueSnackbar(<FormattedMessage id='user.fetch.msg.success' defaultMessage='User successfully fetched' />, {
          variant: 'success',
        });
        setData({ isFetching: false, user });
      } catch (error) {
        enqueueSnackbar(<FormattedMessage id='user.fetch.msg.error' defaultMessage='User fetch failed' />, {
          variant: 'error',
        });
        setData({ isFetching: false });
      }
    };

    fetchData();
  }, []);

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: ''
    },
    validationSchema: PasswordSchema,
    onSubmit: handlePasswordUpdate
  });

  const { errors, touched, handleSubmit, getFieldProps, isSubmitting, dirty, isValid } = formik;

  return (
    <Dialog
      open={true}
      onClose={onClose}
      title={<FormattedMessage id='user.profile.title' defaultMessage='Profile' />}
    >
      {
        data.isFetching ?
          <Stack alignItems="center">
            <CircularProgress />
          </Stack> :
          <>

            <Accordion expanded={expanded === 'panel2'} onChange={handleChange('panel2')}>
              <AccordionSummary
                sx={{ backgroundColor: 'rgba(0, 0, 0, 0.03)' }}
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1bh-content"
                id="panel1bh-header"
              >
                <Typography sx={{ width: '33%', flexShrink: 0 }}>
                  <FormattedMessage id='user.profile.info.title' defaultMessage='Personal info' />
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <div style={{ paddingTop: 10 }}>
                  {
                    data.user ?
                      <UserForm
                        user={data.user}
                        onSubmit={handleUserUpdate}
                        onClose={onClose}
                        changableLocations={false}
                      /> :
                      <FormattedMessage id='common.noData' defaultMessage='No data' />
                  }
                </div>
              </AccordionDetails>
            </Accordion>

            <Accordion expanded={expanded === 'panel1'} onChange={handleChange('panel1')}>
              <AccordionSummary
                sx={{ backgroundColor: 'rgba(0, 0, 0, 0.03)' }}
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1bh-content"
                id="panel1bh-header"
              >
                <Typography sx={{ width: '33%', flexShrink: 0 }}>
                  <FormattedMessage id='user.profile.changePassword.title' defaultMessage='Change password' />
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormikProvider value={formik}>
                  <Form autoComplete="off" noValidate onSubmit={handleSubmit} style={{ paddingTop: 10 }}>
                    <Stack spacing={3}>
                      <TextField
                        required
                        fullWidth
                        autoComplete="current-password"
                        type={showPassword ? 'text' : 'password'}
                        label={intl.formatMessage({
                          id: 'user.form.label.password',
                          defaultMessage: 'Password'
                        })}
                        {...getFieldProps('password')}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton edge="end" onClick={() => setShowPassword((prev) => !prev)}>
                                <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                        error={Boolean(touched.password && errors.password)}
                        helperText={touched.password && errors.password && <FormattedMessage id={errors.password} defaultMessage={errors.password} />}
                      />

                      <TextField
                        required
                        fullWidth
                        autoComplete="confirm-password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        label={intl.formatMessage({
                          id: 'user.form.label.confirmPassword',
                          defaultMessage: 'Confirm Password'
                        })}
                        {...getFieldProps('confirmPassword')}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton edge="end" onClick={() => setShowConfirmPassword((prev) => !prev)}>
                                <Iconify icon={showConfirmPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                        error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                        helperText={touched.confirmPassword && errors.confirmPassword && <FormattedMessage id={errors.confirmPassword} defaultMessage={errors.confirmPassword} />}
                      />
                      <Grid container justifyContent="flex-end">
                        <LoadingButton type="submit" loading={isSubmitting} disabled={!dirty || !isValid} >
                          <FormattedMessage id={'common.button.updatePassword'} defaultMessage='Update password' />
                        </LoadingButton>
                      </Grid>
                    </Stack>
                  </Form>
                </FormikProvider>
              </AccordionDetails>
            </Accordion>
          </>
      }
    </Dialog >
  );
}

UserProfileDialog.propTypes = {
  onClose: PropTypes.func.isRequired
};
